import React, { Component } from "react";
import GoogleSignInButton from "./GoogleSignInButton";
import OtpLoginForm from "./OtpLoginForm";
import UsersService from "../services/UsersService";
import { applyAuthSession } from "../utility/applyAuthSession";
import Modal, { FormGroup, FormControl, FormSelect } from "./ui/Modal";
import Button from "./ui/Button";

class LoginPopup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      phone_no: "",
      password: "",
      accountType: props?.defaultAccountType || "student",
      validationErrors: {
        phone_no: "",
        password: "",
      },
      googleError: "",
      // Mobile OTP login: `otpConfig` stays null (option hidden) unless the admin has switched it
      // on in /admin/settings and the SMS gateway is complete - see componentDidMount.
      otpConfig: null,
      loginMode: "password", // "password" | "otp"
    };

    this.changePhoneNumberHandler = this.changePhoneNumberHandler.bind(this);
    this.changePasswordHandler = this.changePasswordHandler.bind(this);
    this.changeAccountTypeHandler = this.changeAccountTypeHandler.bind(this);
    this.saveUser = this.saveUser.bind(this);
    this.validateForm = this.validateForm.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
    UsersService.getOtpLoginConfig()
      .then((response) => {
        if (this.mounted && response.data?.enabled) this.setState({ otpConfig: response.data });
      })
      .catch(() => {
        // Backend unreachable or OTP not offered: the popup just stays password-only.
      });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  changePhoneNumberHandler = (event) => {
    this.setState({ phone_no: event.target.value });
  };

  changePasswordHandler = (event) => {
    this.setState({ password: event.target.value });
  };

  changeAccountTypeHandler = (event) => {
    this.setState({ accountType: event.target.value });
  };

  validateForm() {
    const { phone_no, password } = this.state;
    let valid = true;
    const validationErrors = {
      phone_no: "",
      password: "",
    };

    if (!phone_no) {
      validationErrors.phone_no = "Phone number is required.";
      valid = false;
    }

    if (!password) {
      validationErrors.password = "Password is required.";
      valid = false;
    }

    this.setState({ validationErrors });
    return valid;
  }

  saveUser = async () => {
    // Reset validation errors
    this.setState({
      validationErrors: {
        phone_no: "",
        password: "",
      },
    });

    // Validate form fields
    if (!this.validateForm()) {
      return; // If validation fails, exit the function
    }

    try {
      const loginResponse = await UsersService.validateLogin(
        this.state.phone_no,
        this.state.password,
        this.state.accountType
      );

      if (loginResponse.data === 0) {
        this.setState({
          validationErrors: {
            ...this.state.validationErrors,
            phone_no: "Invalid Phone Number",
          },
        });
        return;
      }

      const roles = Array.isArray(loginResponse.data.roles) ? loginResponse.data.roles : [];

      const requestedType = (this.state.accountType || "student").toLowerCase();
      const isAdmin = roles.includes("ROLE_ADMIN");
      const isCollege = roles.includes("ROLE_COLLEGE");
      const isAuthor = roles.includes("ROLE_AUTHOR");
      const isStudentOnly = !isAdmin && !isCollege && !isAuthor;

      const roleMatchesRequestedType =
        requestedType === "admin"
          ? isAdmin
          : requestedType === "college"
          ? isCollege
          : requestedType === "author"
          ? isAuthor
          : requestedType === "student"
          ? isStudentOnly
          : true;

      if (!roleMatchesRequestedType) {
        const expectedLabel = isAdmin ? "Admin" : isCollege ? "College" : isAuthor ? "Author" : "Student";
        this.setState({
          validationErrors: {
            ...this.state.validationErrors,
            password: `Selected role does not match this account. Please login as ${expectedLabel}.`,
          },
        });
        return;
      }

      applyAuthSession(loginResponse.data);

      this.props.onClose();
      if (roles.includes("ROLE_ADMIN") && this.props.onAdminLogin) {
        this.props.onAdminLogin();
      } else {
        this.props.onDefaultLogin();
      }
    } catch (error) {
      const status = error?.response?.status;
      const backendMessage =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string" ? error.response.data : null);

      if (status === 401) {
        this.setState({
          validationErrors: {
            ...this.state.validationErrors,
            password: backendMessage || "Invalid phone number or password.",
          },
        });
        return;
      }

      if (status === 403) {
        this.setState({
          validationErrors: {
            ...this.state.validationErrors,
            password: backendMessage || "Account is not active yet.",
          },
        });
        return;
      }

      this.setState({
        validationErrors: {
          ...this.state.validationErrors,
          password: backendMessage || "Login failed. Please try again.",
        },
      });
      console.error("Login error", error);
      return;
    }
  };

  // Called once the backend has accepted a sign-in that doesn't go through the password form -
  // Google (GoogleSignInButton, including its mobile-number step) or mobile OTP (OtpLoginForm) -
  // and returned a session.
  handleSessionSuccess = (data) => {
    const { roles } = applyAuthSession(data);
    this.props.onClose();
    if (roles.includes("ROLE_ADMIN") && this.props.onAdminLogin) {
      this.props.onAdminLogin();
    } else {
      this.props.onDefaultLogin();
    }
  };

  renderLoginModeTabs() {
    const tab = (mode, label) => {
      const selected = this.state.loginMode === mode;
      return (
        <button
          key={mode}
          type="button"
          role="tab"
          aria-selected={selected}
          onClick={() => this.setState({ loginMode: mode })}
          className={
            "flex-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors " +
            (selected ? "bg-white text-brand-700 shadow-sm" : "text-gray-500 hover:text-gray-700")
          }
        >
          {label}
        </button>
      );
    };
    return (
      <div role="tablist" className="mt-3 flex gap-1 rounded-xl bg-gray-100 p-1">
        {tab("password", "Password")}
        {tab("otp", "Mobile OTP")}
      </div>
    );
  }

  render() {
    const { validationErrors, googleError, otpConfig, loginMode, accountType } = this.state;
    // Admins always use a password (the server refuses OTP for them too), so the option isn't
    // even offered once Admin is selected.
    const otpOffered = Boolean(otpConfig) && accountType !== "admin";
    const useOtp = otpOffered && loginMode === "otp";

    return (
      <Modal
        show={true}
        onClose={this.props.onClose}
        title={
          <span className="flex items-center gap-2">
            <img src="/images/bvlogo.png" alt="BharatVidyarthi" className="h-6 w-6 object-contain" />
            Log In
          </span>
        }
        footer={
          useOtp ? null : (
            <Button
              variant="primary"
              id="sign-in-button"
              onClick={this.saveUser}
              className="w-full"
            >
              Log In
            </Button>
          )
        }
      >
        <FormGroup label="Login as" htmlFor="formAccountType">
          <FormSelect
            id="formAccountType"
            value={this.state.accountType}
            onChange={this.changeAccountTypeHandler}
          >
            <option value="student">Student</option>
            <option value="college">College</option>
            <option value="author">Author</option>
            <option value="admin">Admin</option>
          </FormSelect>
        </FormGroup>

        {otpOffered && this.renderLoginModeTabs()}

        {useOtp ? (
          <OtpLoginForm
            key={accountType}
            accountType={accountType}
            otpLength={otpConfig.otpLength}
            onSuccess={this.handleSessionSuccess}
          />
        ) : (
          <form onSubmit={(e) => e.preventDefault()}>
            <FormGroup label="Phone number" htmlFor="formPhone" error={validationErrors.phone_no}>
              <FormControl
                id="formPhone"
                type="tel"
                placeholder="Enter phone number"
                value={this.state.phone_no}
                onChange={this.changePhoneNumberHandler}
                isInvalid={validationErrors.phone_no !== ""}
              />
            </FormGroup>
            <FormGroup label="Password" htmlFor="formPassword" error={validationErrors.password}>
              <FormControl
                id="formPassword"
                type="password"
                placeholder="Enter password"
                value={this.state.password}
                onChange={this.changePasswordHandler}
                isInvalid={validationErrors.password !== ""}
              />
            </FormGroup>
          </form>
        )}

        <div className="mt-4 flex items-center gap-3 text-xs text-gray-400">
          <div className="h-px flex-1 bg-gray-200" />
          or
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        <div className="mt-4 flex justify-center">
          <GoogleSignInButton
            accountType={this.state.accountType}
            onSuccess={this.handleSessionSuccess}
            onError={(message) => this.setState({ googleError: message })}
          />
        </div>
        {googleError && <p className="mt-2 text-sm text-red-600 text-center">{googleError}</p>}
      </Modal>
    );
  }
}

export default LoginPopup;
